import React, { useEffect, useRef, useState } from "react";
import MobileFilters from "components/Filters/MobileFilters";
import { useLazyFetchFlightsQuery } from "redux/services/flightSearch";
import { useSelector } from "react-redux";
import { UseAppDispatch } from "redux/store";
import { searchQuerySelector, updateSearchQuery } from "redux/searchQuery";
import useSetQueryParams from "hooks/useSetQueryParams";

import {
  changeSearchRequestId,
  setSearchForm,
  searchFormSelector,
} from "redux/searchForm";
import useQueryParams, { IQueryParams } from "hooks/useQueryParams";
import {
  setSelectedFareId,
  setSelectedItineraries,
  selectedItinerarySelector,
  itinerarySelector,
} from "redux/selectedItinerary";
import { useTranslations } from "hooks/useTranslations";
import { validateSearchForm } from "validation/validateSearch";
import { ISearchFormValidation } from "validation/validation.interfaces";
import { ErrorHandler } from "components/ResponsePopup/ErrorHandler";
import {
  CustomAnimation,
  IItinerary,
  NoFlightsGraphic,
  MediaQuery,
  isDiff,
  useConfig,
} from "@thekilroygroup/shared-ui";
import {
  backgroundAnimations,
  navigateDelay,
  searchHeaderAnimations,
} from "./animations.searchResultsPage";
import { SearchResultsLoader } from "components/SearchResults/SearchResultsLoader";
import { GraphicMessage } from "components/GraphicMessage/GraphicMessage";

import { Box } from "@mui/material";
import { buildQueryString } from "utils/queryStrings";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  MobileHeader,
  MobileSearchHeader,
  DesktopHeader,
  DesktopSearchHeader,
} from "components/Header";
import { MetapartnerTracking } from "components/MetapartnerTracking/MetapartnerTracking";
import { SearchResultsContainer } from "components/SearchResults/SearchResultsContainer";
import { resetFilters } from "components/Filters/FilterOptions/resetFilters";
import { filterSelector } from "redux/filters/filters.selector";
import { setEntireFilters } from "redux/filters/filters.slice";

const SearchResultsPage = () => {
  const dispatch = UseAppDispatch();
  const searchQuery = useSelector(searchQuerySelector);
  const searchForm = useSelector(searchFormSelector);
  const params: IQueryParams = useQueryParams();
  const [searchParams] = useSearchParams();
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  useSetQueryParams();
  const { translations } = useTranslations();
  const { appConfig } = useConfig();

  const [trigger, result] = useLazyFetchFlightsQuery();
  const { isFetching, isError, data, status } = result;
  const [pageIsMounted, setPageIsMounted] = useState(true);
  const navigate = useNavigate();
  const { fareId } = useSelector(selectedItinerarySelector);
  const itinerary = useSelector(itinerarySelector);
  const allFilters = useSelector(filterSelector);
  const [trackMetapartner, setTrackMetapartner] = useState(false);
  const [trackedPrice, setTrackedPrice] = useState(0);

  const desktopHeaderRef = useRef();

  const triggerRefetch = () => {
    trigger({ searchForm: params.searchQuery, config: appConfig });
  };

  useEffect(() => {
    dispatch(setSearchForm(params.searchQuery));
    dispatch(setSelectedFareId(params.fareId));
    dispatch(updateSearchQuery(params.searchQuery));

    const { isValid }: ISearchFormValidation = validateSearchForm(
      params.searchQuery.flights,
    );

    if (isValid) {
      triggerRefetch();
    }

    if (searchParams.get("utm_source")) {
      const { initialFilters, filters } = resetFilters(
        searchForm,
        allFilters,
        translations,
      );

      if (isDiff(initialFilters, filters)) {
        dispatch(setEntireFilters(initialFilters));
      }
    }

    window.scrollTo({
      behavior: "smooth",
      top: 0,
    });
  }, []);

  const { isValid }: ISearchFormValidation = validateSearchForm(
    searchQuery.flights,
  );

  useEffect(() => {
    if (isError && !isFetching) {
      setShowErrorMessage(true);
    }
  }, [isFetching]);

  const navigateToLowpriceCalendar = () => {
    if (isValid) {
      setTimeout(() => {
        navigate(`/calendar?${buildQueryString(searchForm)}`);
      }, navigateDelay);
    }
  };

  useEffect(() => {
    if (
      !isError &&
      !isFetching &&
      status === "fulfilled" &&
      data?.apiResponse
    ) {
      dispatch(changeSearchRequestId(data.apiResponse.searchRequestId));

      if (fareId) {
        const nextSelectedItinerary = data.apiResponse.itineraries.filter(
          (itinerary: IItinerary) => itinerary?.id === fareId,
        );
        if (nextSelectedItinerary.length) {
          dispatch(setSelectedItineraries([nextSelectedItinerary[0]]));
          // Update the tracked price to the selected itinerary
          setTrackedPrice(nextSelectedItinerary[0]?.totalFareAmounts.total);
        } else {
          // If the fareId is not found, the tracked price is set to 0
          setTrackedPrice(0);
          // If the fareId is not found, the first itinerary is selected
          dispatch(setSelectedItineraries([data.apiResponse.itineraries[0]]));
        }
        // If there is a fareId, the metapartner will be tracked
        setTrackMetapartner(true);
      } else if (data.apiResponse.itineraries.length) {
        dispatch(setSelectedItineraries([data.apiResponse.itineraries[0]]));
        // Update the tracked price to the first itinerary
        setTrackedPrice(data.apiResponse.itineraries[0].totalFareAmounts.total);
        // If there is a fareId, the metapartner will NOT be tracked
        setTrackMetapartner(false);
      }
    }
  }, [result]);

  const showGraphicMessage =
    (!isFetching && !showErrorMessage && (!isValid || isError)) ||
    data?.meta.response.status === 204;

  const showContent =
    isValid && !isFetching && !isError && data?.apiResponse?.itineraries.length;

  return (
    <>
      <CustomAnimation in {...backgroundAnimations}>
        <Box sx={{ backgroundColor: "grey.100", minHeight: "100vh" }}>
          <MediaQuery max="md">
            <MobileHeader
              top={
                <MobileSearchHeader
                  navigateToLowpriceCalendar={navigateToLowpriceCalendar}
                  triggerFetch={() => triggerRefetch()}
                />
              }
              bottom={<MobileFilters />}
            />
          </MediaQuery>
          <MediaQuery min="md">
            <Box ref={desktopHeaderRef}>
              <DesktopHeader>
                <CustomAnimation {...searchHeaderAnimations} in={pageIsMounted}>
                  <DesktopSearchHeader
                    desktopHeaderRef={desktopHeaderRef}
                    triggerFetch={() => triggerRefetch()}
                  />
                </CustomAnimation>
              </DesktopHeader>
            </Box>
          </MediaQuery>
          {isFetching && (
            <SearchResultsLoader
              title={translations.searchResultsPage.loader.isSearchingTitle}
              teaserText={translations.searchResultsPage.loader.teaserText}
            />
          )}
          {isError && showErrorMessage && (
            <ErrorHandler
              errorResult={result}
              setShowErrorPopup={setShowErrorMessage}
              triggerRefetch={triggerRefetch}
            />
          )}
          {showContent && (
            <SearchResultsContainer
              data={data.apiResponse}
              isFetching={isFetching}
              pageIsMounted={pageIsMounted}
              setPageIsMounted={setPageIsMounted}
            />
          )}
          {showGraphicMessage && (
            <GraphicMessage
              title={translations.searchResults.noResults.title}
              subTitle={translations.searchResults.noResults.subTitle}
            >
              <NoFlightsGraphic />
            </GraphicMessage>
          )}
        </Box>
      </CustomAnimation>
      {!isFetching &&
        status === "fulfilled" &&
        trackMetapartner &&
        params.searchQuery.utm_source && (
          <MetapartnerTracking
            selectedItinerary={itinerary}
            eventType="pre-conversion"
            createdDate={new Date().toISOString()}
            utm_source={params.searchQuery.utm_source}
            conversion_id=""
            searchQuery={params.searchQuery}
            price={trackedPrice}
          />
        )}
    </>
  );
};

export default SearchResultsPage;
